import { Location } from 'history';
import { ACTION_TO_ADD_POST, TO_action_YOU_HAVE_TO_BE_loggedIn, TO_BE_LOGGED_IN } from 'mk/autogenerated/translations/HomeRight.062116e5a66d420fdb655a71ac52bd5f'
import { linebreaksbr } from 'mk/bazaar/common/filters';
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import { login_url } from 'mk/urls/functions';
import { CounsellingEntity, PostEntity } from 'mk2/apps/forum/schemas';
import ContestPosts from 'mk2/apps/home/components/ContestPosts';
import Counselling, { CounsellingPlaceholder } from 'mk2/apps/home/components/Counselling';
import ForumPosts, { ForumPostsPlaceholder } from 'mk2/apps/home/components/ForumPosts';
import Photos, { PhotosPlaceholder } from 'mk2/apps/home/components/Photos';
import StrollersReviews, { StrollersReviewsPlaceholder } from 'mk2/apps/home/components/Strollers';
import SubscribedPhotoblogs from 'mk2/apps/home/components/SubscribedPhotoblogs';
import WikiExperiences, { WikiExperiencesPlaceholder } from 'mk2/apps/home/components/WikiExperiences';
import { Props } from 'mk2/apps/home/containers/Home/HomePage';
import AdformInview from 'mk2/components/AdformInview';
import Interpolate from 'mk2/components/Interpolate';
import AdSlot from 'mk2/containers/AdSlot/AdSlot';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import * as React from 'react';
import styles from './HomeRight.mscss';

const HomeRight: React.FunctionComponent<Props> = ({
    loadingState,
    forumPosts,
    wikiExperiences,
    photoblogPosts,
    requestUser,
    counsellings,
    strollers,
    onShowWarningToast,
    lastVisitCountFriends,
    mobile,
    onStoreEvent,
    contestTopic,
    contestPosts,
    location,
    forumPostsVariant,
}) => {
    const isLoggedIn = isAuthenticated(requestUser);
    const showForumPosts = !isSuccess(forumPostsVariant.loadingState) || forumPostsVariant.variant !== 'yes';

    return (
        <div className={styles.HomeRight__right}>
            {loadingState === LoadingState.INIT ||
            (loadingState === LoadingState.LOADING && !forumPosts.length) ? (
                <>
                    <ForumPostsPlaceholder className={styles.HomeRight__forumPosts} />
                    <WikiExperiencesPlaceholder className={styles.HomeRight__wikiExperiences} />
                    <PhotosPlaceholder className={styles.HomeRight__photos} />
                    <StrollersReviewsPlaceholder className={styles.HomeRight__strollers} />
                    <CounsellingPlaceholder className={styles.HomeRight__counselling} />
                </>
            ) : isSuccess(loadingState) || forumPosts.length ? (
                <>
                    <AdformInview
                        placementId={ACTIVE_SERVER_ID === 201 ? 544773 : 402804}
                        className={styles.HomeRight__adformInview}
                        passback={
                            <>
                                <AdSlot
                                    zone="Homepage - Rectangle 1 (Desktop)"
                                    className={styles.HomeRight__adSlot__desktop}
                                />
                                <AdSlot
                                    zone="Homepage - Rectangle 2 (Mobile)"
                                    className={styles.HomeRight__adSlot__mobile}
                                />
                            </>
                        }
                    />
                    {isAuthenticated(requestUser) && (
                        <SubscribedPhotoblogs
                            className={styles.HomeRight__subscribedPhotoblogs}
                            lastVisitCountFriends={lastVisitCountFriends}
                        />
                    )}
                    {contestPosts.length > 0 && (
                        <ContestPosts
                            className={styles.HomeRight__contestPosts}
                            posts={contestPosts}
                            topic={contestTopic}
                        />
                    )}
                    {showForumPosts && (
                        <ForumPosts posts={forumPosts} className={styles.HomeRight__forumPosts} />
                    )}
                    <WikiExperiences
                        className={styles.HomeRight__wikiExperiences}
                        experiences={wikiExperiences}
                    />
                    <Photos className={styles.HomeRight__photos} posts={photoblogPosts} />
                    {strollers && (
                        <StrollersReviews
                            className={styles.HomeRight__strollers}
                            strollers={strollers}
                        />
                    )}
                    {counsellings &&
                        counsellings.map((counselling) => {
                            if (counselling.posts.length > 0) {
                                return (
                                    <CounsellingItem
                                        key={counselling.id}
                                        posts={counselling.posts}
                                        counselling={counselling.counselling}
                                        className={styles.HomeRight__counselling}
                                        isLoggedIn={isLoggedIn}
                                        location={location}
                                        onShowWarningToast={onShowWarningToast}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })}
                </>
            ) : null}
        </div>
    );
};

HomeRight.displayName = 'HomeRight';

export default HomeRight;

interface CounsellingItemProps {
    className?: string;
    posts: PostEntity[];
    counselling: CounsellingEntity;
    isLoggedIn: boolean;
    location: Location;

    onShowWarningToast(message: string | React.ReactNode);
}

const CounsellingItem: React.FunctionComponent<CounsellingItemProps> = ({
    className,
    posts,
    counselling,
    isLoggedIn,
    onShowWarningToast,
    location,
}) => {
    const onAddPostClick = useEventCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (!isLoggedIn) {
                event.preventDefault();
                onShowWarningToast(
                    <Interpolate
                        i18nKey={TO_action_YOU_HAVE_TO_BE_loggedIn}
                        action={ACTION_TO_ADD_POST}
                        loggedIn={(
                            <a href={login_url({ next: location.pathname })}>{TO_BE_LOGGED_IN}</a>
                        )}
                    />,
                );
            } else if (counselling.isLocked) {
                event.preventDefault();
                onShowWarningToast(linebreaksbr(counselling.lockedMessage));
            }
        },
        [isLoggedIn, counselling, onShowWarningToast, location],
    );

    return (
        <Counselling
            posts={posts}
            counselling={counselling}
            onAddPostClick={onAddPostClick}
            className={styles.HomeRight__counselling}
        />
    );
};
